import React, { useEffect, useRef, useState } from 'react';
import { statusCheckInErrorMessage } from '../statusCheckInError';

export const STATUS_OPTIONS = [
  { value: 'great', emoji: '🔥', label: 'Feeling great' },
  { value: 'good', emoji: '😊', label: 'Feeling good' },
  { value: 'okay', emoji: '😐', label: 'Feeling okay' },
  { value: 'tired', emoji: '😴', label: 'Feeling tired' },
  { value: 'stressed', emoji: '😤', label: 'Feeling stressed' },
  { value: 'recovering', emoji: '🩹', label: 'Recovering' },
  { value: 'sick', emoji: '🤒', label: 'Feeling sick' },
  { value: 'injured', emoji: '🤕', label: 'Injured' },
  { value: 'complicated', emoji: '🧭', label: 'Something else' },
];

export const StatusDropdown = React.memo(function StatusDropdown({ currentStatus, onStatusChange, isSubmitting }) {
  const [isOpen, setIsOpen] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);
  const [focusedOption, setFocusedOption] = useState(0);

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) optionRefs.current[focusedOption]?.focus();
  }, [isOpen, focusedOption]);

  const openWithFocus = index => {
    setFocusedOption(index);
    setIsOpen(true);
  };

  const handleTriggerKeyDown = event => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      openWithFocus(event.key === 'ArrowUp' ? STATUS_OPTIONS.length - 1 : 0);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleOptionKeyDown = (event, index) => {
    let next = null;
    if (event.key === 'ArrowDown') next = (index + 1) % STATUS_OPTIONS.length;
    else if (event.key === 'ArrowUp') next = (index - 1 + STATUS_OPTIONS.length) % STATUS_OPTIONS.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = STATUS_OPTIONS.length - 1;
    else if (event.key === 'Escape') {
      event.preventDefault();
      setIsOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (next !== null) {
      event.preventDefault();
      setFocusedOption(next);
    }
  };

  const current = STATUS_OPTIONS.find(status => status.value === currentStatus);

  const chooseStatus = async option => {
    setError('');
    setIsOpen(false);
    if (option.value === 'complicated') {
      setContextOpen(true);
      return;
    }
    setContextOpen(false);
    const saved = await onStatusChange(option.value);
    if (saved === false || saved?.ok === false) {
      setError(saved?.message ? statusCheckInErrorMessage({ message: saved.message }) : 'We couldn’t save your check-in. Please try again.');
      return;
    }
    setNote('');
  };

  const saveComplicated = async () => {
    const trimmed = note.trim();
    if (!trimmed || isSubmitting) return;
    const saved = await onStatusChange('complicated', trimmed);
    if (saved === false || saved?.ok === false) {
      setError(saved?.message ? statusCheckInErrorMessage({ message: saved.message }) : 'We couldn’t save your check-in. Please try again.');
      return;
    }
    setContextOpen(false);
    setNote('');
    setError('');
  };

  return (
    <div className="status-dropdown-container" ref={dropdownRef}>
      <div className="status-dropdown-inner">
        <button
          ref={triggerRef}
          className={`status-dropdown-trigger ${isOpen ? 'open' : ''} ${isSubmitting ? 'submitting' : ''}`}
          aria-controls="feed-status-options"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onKeyDown={handleTriggerKeyDown}
          onClick={() => !isSubmitting && setIsOpen(!isOpen)}
          disabled={isSubmitting}
        >
          <span className="status-dropdown-emoji">{current ? current.emoji : '🌡️'}</span>
          <span className="status-dropdown-label">{current ? current.label : 'How do you feel?'}</span>
          <span className="status-dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
        </button>
        {isOpen && (
          <div id="feed-status-options" role="listbox" className="status-dropdown-menu">
            {STATUS_OPTIONS.map((option, index) => (
              <button
                ref={element => { optionRefs.current[index] = element; }}
                role="option"
                aria-selected={currentStatus === option.value}
                key={option.value}
                className={`status-dropdown-option ${currentStatus === option.value ? 'selected' : ''}`}
                onKeyDown={event => handleOptionKeyDown(event, index)}
                onClick={() => chooseStatus(option)}
              >
                <span className="status-option-emoji">{option.emoji}</span>
                <span className="status-option-label">{option.label}</span>
                {currentStatus === option.value && <span className="status-option-check">✓</span>}
              </button>
            ))}
          </div>
        )}
        {error && !contextOpen && <p className="status-dropdown-context-error" role="alert">{error}</p>}
        {contextOpen && (
          <div className="status-dropdown-context">
            <label htmlFor="status-dropdown-context-note">
              What’s making today complicated?
              <span>Private to you and your Coach</span>
            </label>
            <textarea
              id="status-dropdown-context-note"
              required
              maxLength={500}
              value={note}
              onChange={event => setNote(event.target.value)}
            />
            <button
              type="button"
              className="status-dropdown-save-context"
              disabled={isSubmitting || !note.trim()}
              onClick={saveComplicated}
            >
              Save check-in
            </button>
            {error && <p className="status-dropdown-context-error" role="alert">{error}</p>}
          </div>
        )}
      </div>
    </div>
  );
});

export default StatusDropdown;
